import React from 'react';
import { ButtonBase } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

interface DashedAddButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  /** When true, stretches to fill the parent (used inside a grid cell). */
  fullHeight?: boolean;
  /** Optional leading icon. Defaults to `<AddIcon />`. Pass `null` to omit. */
  icon?: React.ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * A dashed outlined button used for "add" actions in the new scenario editor.
 * Comes in two variants:
 * - Inline (default): full-width button with small padding, used inside lists (Facts, Locations).
 * - Full-height: fills its parent (e.g. a grid cell) with an icon above the text, used as
 *   a skeleton add-item card (Personas).
 */
const DashedAddButton: React.FC<DashedAddButtonProps> = ({
  onClick,
  children,
  fullHeight = false,
  icon = <AddIcon sx={{ fontSize: '1.25rem' }} />,
  sx
}) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      width: '100%',
      border: '1px dashed',
      borderColor: 'grey.300',
      borderRadius: '6px',
      color: 'grey.500',
      fontSize: fullHeight ? '0.8125rem' : '0.75rem',
      fontWeight: 500,
      transition: 'all 0.15s',
      '&:hover': {
        borderColor: 'secondary.main',
        color: 'secondary.main',
        backgroundColor: 'rgba(245, 122, 61, 0.05)'
      },
      ...(fullHeight
        ? {
            height: '100%',
            minHeight: 120,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5
          }
        : {
            p: 1,
            mt: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5
          }),
      ...sx
    }}
  >
    {icon}
    {children}
  </ButtonBase>
);

export default DashedAddButton;
